<script setup lang="ts">import { ref as _ref } from 'vue';

import moment from "moment";
import { useRouter } from "vue-router";
import { computed, watch, ref } from "vue";

import { caseService } from "@/services";
import { $can, fromUtc } from "@/services/UtilitiesService";

import { useBrandStore } from "@/stores/brand-store";

import ConfirmPopover from "@/components/ConfirmPopover.vue";

import type { UserCaseHistory } from "@/models/UserCaseHistory";

const router = useRouter();
const brandStore = useBrandStore();

let caseHistory = _ref<UserCaseHistory[]>([]);
const brandRegex = computed(() =>
  brandStore.selectedBrand == null ? null : new RegExp(`^${brandStore.selectedBrand}$`, "i"),
);
const allCases = ref(false);
const loading = ref(true); // Set initially to true to prevent the UI tests picking it up prematurely

const selectedClient = computed(() => useBrandStore().selectedClient);

const selectedHistory = computed(() => {
  if (allCases.value) return [...caseHistory.value].sort((l, r) => r.accessed.localeCompare(l.accessed));

  const regex = brandRegex.value;
  return regex == null ? caseHistory.value : caseHistory.value.filter((f) => regex.test(f.brand));
});

const canUseCaseHistory = computed(() => brandStore.featuresConfig.caseHistory === "enabled" && $can("useCaseHistory"));

watch(
  [canUseCaseHistory, selectedClient],
  () => {
    if (canUseCaseHistory.value) getCaseHistory();
    else router.push({ name: "brand", params: { brand: brandStore.selectedBrand } });
  },
  { immediate: true },
);

function fromNow(time: string) {
  return moment.utc(time).fromNow();
}

async function remove(history: UserCaseHistory): Promise<void> {
  if (loading.value || selectedClient.value == null) return;

  loading.value = true;

  try {
    await caseService.DeleteUserCaseHistory(selectedClient.value, history.brand, history.caseReference);
    await getCaseHistory();
  } finally {
    loading.value = false;
  }
}

async function getCaseHistory() {
  if (selectedClient.value == null) return;

  loading.value = true;
  try {
    caseHistory.value = await caseService
      .GetUserCaseHistory(selectedClient.value)
      .then((r) => r.sort((l, r) => r.accessed.localeCompare(l.accessed) || l.order - r.order));
  } finally {
    loading.value = false;
  }
}
</script>
<template>
  <div
    id="caseHistory"
    :data-loading="`${loading}`"
    class="tw-m-4 tw-flex tw-flex-col tw-rounded-sm tw-bg-white tw-pt-2 tw-shadow-inner"
  >
    <h1 class="tw-sr-only">Case History</h1>
    <div class="tw-flex tw-justify-between">
      <h4 class="tw-mb-2 tw-px-2 tw-text-lg tw-font-semibold tw-tracking-wide">Recently accessed cases</h4>
      <i v-if="loading" class="fas fa-circle-notch fa-spin tw-mx-3 tw-my-auto tw-text-center"></i>
    </div>
    <div class="tw-px-2">
      <input id="show-all-cases" v-model="allCases" type="checkbox" />
      <label for="show-all-cases" class="tw-mx-2 tw-cursor-pointer">Include cases from all projects</label>
    </div>
    <div class="tw-divide-y tw-divide-black/20 tw-pt-2">
      <div
        v-for="caseHistory in selectedHistory"
        :key="`${caseHistory.brand}-${caseHistory.caseReference}`"
        class="tw-flex tw-justify-between tw-px-2 tw-py-1"
      >
        <div class="tw-flex tw-items-center tw-gap-2 tw-font-medium">
          <ConfirmPopover
            :key="caseHistory.order"
            confirm-text="Delete"
            :trigger-props="{ asChild: true }"
            @confirmed="() => remove(caseHistory)"
          >
            <template #trigger>
              <button
                type="button"
                class="tw-grid tw-size-6 tw-place-items-center tw-rounded-full tw-p-1 hover:tw-bg-gray-500/20"
              >
                <span class="tw-sr-only">Delete {{ caseHistory.caseReference }} from case history</span>
                <i class="fa fa-trash"></i>
              </button>
            </template>
            <template #question>
              Are you sure you want to remove case "{{ caseHistory.caseReference }}" from this list?
            </template>
          </ConfirmPopover>
          <router-link
            v-if="caseHistory.exists"
            :to="`/${caseHistory.brand.toUpperCase()}/${caseHistory.caseReference}`"
          >
            <span class="tw-sr-only">Go to case </span>
            <i class="fa fa-fw fa-external-link-alt tw-text-gray-700" title="View case"></i>
            [{{ caseHistory.brand.toUpperCase() }}]
            {{ caseHistory.caseReference }}
          </router-link>
          <template v-else>
            <span class="tw-sr-only">Go to case </span>
            <i class="fa fa-fw tw-ml-2"></i>
            <span>[{{ caseHistory.brand.toUpperCase() }}] {{ caseHistory.caseReference }}</span>
          </template>
        </div>
        <div class="tw-self-center">
          <span
            class="tw-mx-2 tw-rounded-sm tw-bg-gray-600 tw-px-1 tw-py-px tw-text-white"
            :title="fromUtc(caseHistory.accessed)"
          >
            <span class="tw-sr-only">Last Accessed on </span>
            {{ fromNow(caseHistory.accessed) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
